import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const modalShellClassName = 'flex max-h-[calc(100dvh-1.25rem)] w-full max-w-[29rem] flex-col overflow-hidden rounded-[1rem] border border-slate-300 bg-white ring-1 ring-slate-200/80 shadow-[0_24px_64px_-28px_rgba(15,23,42,0.42)] min-[641px]:max-h-[calc(100dvh-3rem)]';
const modalHeaderClassName = 'shrink-0 px-4 pt-5 min-[641px]:px-6 min-[641px]:pt-6';
const modalBodyClassName = 'min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-5 pt-3 min-[641px]:px-6';
const modalFooterClassName = 'flex shrink-0 flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50/70 px-4 py-3 min-[641px]:flex-row min-[641px]:justify-end min-[641px]:px-6 min-[641px]:py-4';
const modalPrimaryButtonClassName = 'inline-flex min-w-[92px] items-center justify-center rounded-[0.65rem] px-3.5 py-2 text-[13px] font-semibold text-white shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';
const modalSecondaryButtonClassName = 'inline-flex min-w-[84px] items-center justify-center rounded-[0.65rem] border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-semibold text-slate-700 shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';

const RestoreConfirmModal = ({
  open,
  orderLabel,
  vin,
  error = null,
  loading = false,
  onCancel,
  onConfirm,
}) => {
  const [confirmed, setConfirmed] = useState(false);
  const vinInUse = error?.code === 'VIN_IN_USE';

  useEffect(() => {
    if (open) setConfirmed(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center overflow-hidden bg-slate-900/28 px-2.5 py-2.5 backdrop-blur-[3px] min-[641px]:px-4 min-[641px]:py-6">
      <div className={modalShellClassName} role="dialog" aria-modal="true" aria-labelledby="restore-order-title">
        <div className={modalHeaderClassName}>
          <div className="inline-flex items-center rounded-[0.55rem] bg-slate-900 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-white">
            Restore order
          </div>
          <div className="mt-2.5">
            <h3 id="restore-order-title" className="text-[0.95rem] font-semibold leading-6 text-slate-900">
              Restore this order?
            </h3>
          </div>
        </div>

        <div className={modalBodyClassName}>
          <p className="text-sm leading-6 text-slate-600">
            This puts the order back on the admin list and the customer's portal. Restore is blocked if another active order already uses this VIN. A new invoice and invoice file are created if payment is still outstanding.
          </p>
          {orderLabel ? (
            <p className="mt-3 break-words text-sm font-medium text-slate-800">{orderLabel}</p>
          ) : null}

          {vinInUse ? (
            <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3.5" role="alert">
              <p className="text-sm font-semibold text-rose-800">This VIN is already in use</p>
              <p className="mt-1 text-sm leading-6 text-rose-700">
                {vin ? `VIN ${vin} is` : 'This VIN is'} already used by another active order. Archive that order first, then you can restore this one.
              </p>
            </div>
          ) : (
            <>
              {error?.message ? (
                <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3.5" role="alert">
                  <p className="text-sm font-semibold text-rose-800">Could not restore this order</p>
                  <p className="mt-1 text-sm leading-6 text-rose-700">{error.message}</p>
                </div>
              ) : null}
              <div className="mt-5 flex items-start rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5">
                <div className="flex h-5 items-center">
                  <input
                    id="restore-order-confirmation"
                    name="restore-order-confirmation"
                    type="checkbox"
                    checked={confirmed}
                    disabled={loading}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-slate-700 focus:ring-slate-700"
                  />
                </div>
                <div className="ml-3 min-w-0 text-sm">
                  <label htmlFor="restore-order-confirmation" className="text-slate-700">
                    I confirm this order should be restored
                  </label>
                </div>
              </div>
            </>
          )}
        </div>

        <div className={modalFooterClassName}>
          {vinInUse ? null : (
            <button
              type="button"
              className={`${modalPrimaryButtonClassName} bg-slate-900 hover:bg-slate-800 focus:ring-slate-400`}
              disabled={loading || !confirmed}
              onClick={onConfirm}
            >
              {loading ? 'Restoring...' : 'Confirm'}
            </button>
          )}
          <button
            type="button"
            className={`${modalSecondaryButtonClassName} hover:border-slate-300 hover:bg-slate-100 focus:ring-slate-300`}
            onClick={onCancel}
            disabled={loading}
          >
            {vinInUse ? 'Close' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RestoreConfirmModal;
